import { cloneDeep } from "lodash";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { treeActions } from "../../../redux/slices/treeSlice";
import { getUpdatedData } from "./NewRecordOutbound";
import { RECORD_TYPE, DATA_TYPE } from "../../../constants/datatypes";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { genKey, getUniqueWorkflowItemIdFromArray } from "../../../utils/utilFunctions";
import { getSelectedNodes } from "./CopyAndPaste";

const NewConditionalELSEField = ({ toggle, openModal }) => {
    const { t } = useTranslation();
    const parentScreen = useContext(ParentScreenCtx);
    const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
    const currentTab = rightTree.currentTab;
    const selectedNode = rightTree.tree[currentTab].selectedNode;
    const templateHeader = rightTree.tree.templateHeader;
    const [showModal, setShowModal] = useState(false);
    const content = t('BOX_LIKE_TO_PLACE_ITEM_INSIDE_CURR_REC_OR_DIRECTLY_AFTER').split(`\\n`).map((str, idx) => <div key={idx}>{str ? str : <br />}</div>);
    const dispatch = useDispatch();
    const contentRef = useRef();
    const handleNewField = (isInside, overrideSelectedNode) => {
        const selectedNode = overrideSelectedNode || rightTree.tree[currentTab].selectedNode;
        let nestedLevels = selectedNode.key.split("-");
        nestedLevels = nestedLevels.splice(1);
        const translatedData = cloneDeep(rightTree.tree[currentTab].translatedData);
        const newId = getUniqueWorkflowItemIdFromArray(translatedData);

        // Function For Getting New Record Title and Key
        const getNewFieldData = (data, key) => {
            //If it is empty so it will make first node with 'ib-0' key
            if (!data.length) return { key: `${key}-0`, title: `${t('UNNAMED')}1`, insertingPosition: 1 };

            let latestNewNodeNum = 0;
            let insertingPosition = 1;
            data.forEach((ele, idx) => {
                // Checking if Unnamed is present already for creating next unnamed record
                const fieldTitle = ele?.MAPNAME;
                if (fieldTitle && fieldTitle.startsWith(`${t("UNNAMED")} ${t("CONDITIONAL_FIELDS_ELSE_NAME")}`)) {
                    const latestUnnamedRecord = Number(fieldTitle.slice(`${t("UNNAMED")} ${t("CONDITIONAL_FIELDS_ELSE_NAME")}`.length));
                    if (!isNaN(latestUnnamedRecord) && DATA_TYPE[templateHeader?.FILETYPE]?.field?.icon === ele?.ICON_DISPLAY) {
                        latestNewNodeNum =
                            latestUnnamedRecord > latestNewNodeNum
                                ? latestUnnamedRecord
                                : latestNewNodeNum;
                    }
                }
                if (ele.key === selectedNode.key) {
                    insertingPosition = idx + 2;
                }
            });

            const lastRecordKey = data[data.length - 1].key.split("-");
            lastRecordKey[lastRecordKey.length - 1] = +lastRecordKey[lastRecordKey.length - 1] + 1;
            const newRecordKey = lastRecordKey.join("-");
            return {
                key: newRecordKey,
                title: `${t('UNNAMED')}${latestNewNodeNum ? +latestNewNodeNum + 1 : 1}`,
                insertingPosition,
            };
        };

        // Inserting New Node Object in Tree Data (Recursive Function);
        function insertNodeInNestedTree(translatedData, nestedLevels, isInside) {
            const level = isInside ? 0 : 1;
            if (nestedLevels.length === level) {
                const { children, key } = translatedData;
                const newFieldDetails = children
                    ? getNewFieldData(children, key)
                    : getNewFieldData(translatedData, "ob");
                //    const NodeId = localStorage.getItem("conditionalIfNodeId")
                //    console.log(selectedNode.nodeId)
                const newNode = {
                    key: newFieldDetails.key,
                    title: `${t("UNNAMED")} ${t("CONDITIONAL_FIELDS_ELSE_NAME")}`,
                    NAME: `UNNAMED (ELSE)`,
                    ICON_DISPLAY: "CONDITION_ELSE",
                    nodeId: selectedNode.nodeId,
                    alternateNodeId: genKey(newFieldDetails.title),
                    // TYPE: "Action",
                    //OUTBOUND_ACTION_NAME: "",
                    children: [],
                    fieldNumber: newFieldDetails.insertingPosition,
                    FIELDTYPE: "ALPHA",
                    ...(parentScreen === "workflow" ? {
                        ID: newId,
                    } : {})
                };

                if (children) {
                    if (isInside) {
                        translatedData.children.splice(0, 0, newNode);
                        let newKey = selectedNode.key.split('-');
                        newKey.push('0')
                        newKey = newKey.join('-');
                        dispatch(treeActions.setRightTreeSelectedNode({
                            screen: parentScreen,
                            type: currentTab,
                            data: { ...newNode, key: newKey }
                        }));
                    } else {
                        if(parentScreen === "workflow"){
                            newNode["ELSE_NAME"] = "Item List (ELSE)";
                            translatedData.children[newFieldDetails.insertingPosition - 2]["ELSE_NAME"] = newNode?.ELSE_NAME;
                            newNode.title = "Item List (ELSE)";
                            newNode.NAME = translatedData.children[newFieldDetails.insertingPosition - 2]?.NAME || "Item List (IF)";
                            newNode["ACTION"] = "ITEM_LIST";
                        }
                        translatedData.children.splice(newFieldDetails.insertingPosition - 1, 0, newNode);
                        let newKey = selectedNode.key.split('-');
                        newKey[newKey.length - 1] = Number(newKey[newKey.length - 1]) + 1;
                        newKey = newKey.join('-');
                        //  newNode.RECORD_TYPE = selectedNode?.RECORD_TYPE;
                        dispatch(treeActions.setRightTreeSelectedNode({
                            screen: parentScreen,
                            type: currentTab,
                            data: { ...newNode, key: newKey }
                        }));
                    }
                }
                return;
            }

            const isArray = Array.isArray(translatedData);
            if (isArray) {
                const nestedData = translatedData[[nestedLevels[0]]];
                const newNestedLevels = nestedLevels.splice(1);
                insertNodeInNestedTree(nestedData, newNestedLevels, isInside);
            } else {
                // const newNestedLevels = nestedLevels.splice(1);
                insertNodeInNestedTree(translatedData.children, nestedLevels, isInside);
            }
        }

        insertNodeInNestedTree(translatedData, nestedLevels, isInside);
        const updatedData = getUpdatedData(translatedData, templateHeader?.FILETYPE,null,parentScreen === "workflow", currentTab);
        dispatch(
            treeActions.setNewRightTreeTranslatedData({
                screen: parentScreen,
                type: currentTab,
                data: updatedData,
            })
        );
        toggle(null);
        setShowModal(false);
    };

    const newConditionalHandler = () => {
        if (selectedNode?.ICON_DISPLAY === "CONDITION_IF") {
            handleNewField(false);
        }else {
            const parentKeys = [];
            const splitedKey = selectedNode?.key?.split("-") || [];
            splitedKey.pop();
            let parentKey = splitedKey.join("-");
            const parentNodes = getSelectedNodes([parentKey], rightTree.tree[currentTab]?.translatedData, true);
            const hasConditionIfParent = parentNodes.some(node => node.ICON_DISPLAY === "CONDITION_IF");
            if (hasConditionIfParent && parentNodes.length === 1) {
                handleNewField(false, parentNodes[0]);
            }
        }
    }
    useEffect(() => {
        if (openModal) {
            newConditionalHandler();
        }
    }, [openModal])

    return (
        <Dialog open={showModal} maxWidth="xs" fullWidth>
            <DialogTitle className="dialog-header">
                <div className="title">
                    {t("BOX_CONFIRM")}
                </div>
            </DialogTitle>
            <DialogContent className="dialog-content" dividers>
                <div>{content}</div>
            </DialogContent>
            <DialogActions>
                <Button size='small' variant="outlined" color="primary" onClick={() => handleNewField(true)} >
                    {t("BTN_YES")}
                </Button>
                <Button size='small' variant="outlined" color="primary" onClick={() => handleNewField(false)} >
                    {t("BTN_NO")}
                </Button>
                <Button size='small' variant="outlined" color="primary" onClick={() => {
                    toggle(null);
                    setShowModal(false);
                }} >
                    {t("BTN_CANCEL")}
                </Button>
            </DialogActions>
        </Dialog>
    )
};


export default NewConditionalELSEField;